import {Fragment} from 'react';
import classNames from 'classnames';
import type {LucideIcon, LucideProps} from 'lucide-react';

/*
 * Every icon in the app is rendered through this component so they are all
 * drawn the same way: a 1.75 stroke that stays 1.75 screen pixels at every
 * size (nonScalingStroke), and the colour of the surrounding text.
 *
 * Icons are 1em square by default and so follow the surrounding font-size.
 * The design-system sizes are xs 16, s 20 (the default in controls), m 24
 * (tool toolbars) and l 32 (empty states); a number gives an exact pixel
 * size. The lg/xl/2xl/2x/3x names are the old FontAwesome classes (see
 * _icons.scss) and are kept for existing call sites.
 *
 * data-icon is kept on the svg because a lot of the SCSS selects icons with
 * svg[data-icon], which FontAwesome used to add.
 */

export type IconSize = 'xs' | 's' | 'm' | 'l' | 'lg' | 'xl' | '2xl' | '2x' | '3x';

const pixelSizes: Partial<Record<IconSize, number>> = {
    xs: 16,
    s: 20,
    m: 24,
    l: 32
};

export interface IconProps extends Omit<LucideProps, 'size' | 'ref'> {
    icon: LucideIcon;
    size?: IconSize | number;
    title?: string;
}

export default function Icon({
    icon: LucideComponent,
    size,
    title,
    className,
    children,
    strokeWidth = 1.75,
    nonScalingStroke = true,
    ...rest
}: IconProps) {
    const pixels =
        typeof size === 'number'
            ? size
            : size
              ? pixelSizes[size]
              : undefined;

    return (
        <LucideComponent
            size={pixels ?? '1em'}
            strokeWidth={strokeWidth}
            nonScalingStroke={nonScalingStroke}
            className={classNames(
                typeof size === 'string' && !pixels && `icon-${size}`,
                className
            )}
            data-icon=""
            {...rest}
        >
            {title ? (
                // Lucide renders children after its own keyed elements.
                <Fragment key="title">
                    <title>{title}</title>
                    {children}
                </Fragment>
            ) : (
                children
            )}
        </LucideComponent>
    );
}
